import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import store from '../store';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const DEFAULT_THEME = {
  sidebarColorStart: '#092E51',
  sidebarColorEnd: '#092E51',
  primaryColor: '#3b82f6',
  secondaryColor: '#8b5cf6',
  backgroundPattern: 'none',
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [customPatterns, setCustomPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load theme from API on mount
  useEffect(() => {
    loadTheme();
  }, []);

  // Apply theme to CSS variables whenever theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const loadTheme = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('usertoken');
      
      if (!token) {
        // No token, use default theme
        const savedTheme = localStorage.getItem('userTheme');
        if (savedTheme) {
          setTheme(JSON.parse(savedTheme));
        }
        setLoading(false);
        return;
      }

      const response = await axios.get(`${config.API_BASE_URL}/api/user-preferences`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.preferences) {
        const prefs = response.data.preferences;
        const loadedTheme = {
          sidebarColorStart: prefs.sidebar_color_start,
          sidebarColorEnd: prefs.sidebar_color_end,
          primaryColor: prefs.primary_color,
          secondaryColor: prefs.secondary_color,
          backgroundPattern: prefs.background_pattern,
        };
        setTheme(loadedTheme);
        localStorage.setItem('userTheme', JSON.stringify(loadedTheme));

        if (prefs.morocco_theme !== undefined) {
          store.dispatch({ type: 'set', moroccoTheme: prefs.morocco_theme });
        }
      }
    } catch (err) {
      console.error('Error loading theme:', err);
      // Fallback to localStorage
      const savedTheme = localStorage.getItem('userTheme');
      if (savedTheme) {
        setTheme(JSON.parse(savedTheme));
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomPatterns = async () => {
    try {
      const token = localStorage.getItem('usertoken');
      if (!token) return;

      const response = await axios.get(`${config.API_BASE_URL}/api/user-preferences/custom-patterns`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setCustomPatterns(response.data.patterns);
      }
    } catch (err) {
      console.error('Error loading custom patterns:', err);
    }
  };

  useEffect(() => {
    loadCustomPatterns();
  }, []);


  const saveTheme = async (newTheme) => {
    try {
      const token = localStorage.getItem('usertoken');
      
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.put(
        `${config.API_BASE_URL}/api/user-preferences`,
        {
          sidebar_color_start: newTheme.sidebarColorStart,
          sidebar_color_end: newTheme.sidebarColorEnd,
          primary_color: newTheme.primaryColor,
          secondary_color: newTheme.secondaryColor,
          background_pattern: newTheme.backgroundPattern,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setTheme(newTheme);
        localStorage.setItem('userTheme', JSON.stringify(newTheme));
        return { success: true };
      }
    } catch (err) {
      console.error('Error saving theme:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const resetTheme = async () => {
    try {
      const token = localStorage.getItem('usertoken');
      
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await axios.delete(`${config.API_BASE_URL}/api/user-preferences`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setTheme(DEFAULT_THEME);
        localStorage.setItem('userTheme', JSON.stringify(DEFAULT_THEME));
        return { success: true };
      }
    } catch (err) {
      console.error('Error resetting theme:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const applyTheme = (themeToApply) => {
    // Apply CSS variables
    document.documentElement.style.setProperty('--sidebar-color-start', themeToApply.sidebarColorStart);
    document.documentElement.style.setProperty('--sidebar-color-end', themeToApply.sidebarColorEnd);
    document.documentElement.style.setProperty('--primary-color', themeToApply.primaryColor);
    document.documentElement.style.setProperty('--secondary-color', themeToApply.secondaryColor);
    
    // Apply background pattern
    applyBackgroundPattern(themeToApply.backgroundPattern);
  };

  const applyBackgroundPattern = (pattern) => {
    console.log('Applying background pattern to sidebar:', pattern);
    
    // Remove pattern class from body (no longer needed)
    const body = document.body;
    body.classList.remove(
      'pattern-none',
      'pattern-dots',
      'pattern-grid',
      'pattern-diagonal',
      'pattern-hexagons',
      'pattern-waves',
      'pattern-triangles',
      'pattern-circuit',
      'pattern-stars',
      'pattern-hearts',
      'pattern-cars',
      'pattern-flowers',
      'pattern-abstract',
      'pattern-blossom',
      'pattern-barcelona'
    );
    
    // Apply pattern to sidebar via CSS custom property
    const patternClass = `sidebar-pattern-${pattern}`;
    
    // Function to apply pattern
    const applyPattern = () => {
      const sidebar = document.querySelector('.sidebar');
      if (sidebar) {
        // Remove all pattern classes
        const classesToRemove = [];
        sidebar.classList.forEach(cls => {
          if (cls.startsWith('sidebar-pattern-')) {
            classesToRemove.push(cls);
          }
        });
        sidebar.classList.remove(...classesToRemove);

        // Reset background image style
        sidebar.style.backgroundImage = '';

        const STANDARD_PATTERNS = [
          'dots', 'grid', 'diagonal', 'hexagons', 'waves',
          'triangles', 'circuit', 'stars', 'hearts', 'cars',
          'flowers', 'abstract', 'blossom', 'barcelona'
        ];
        
        const isCustom = pattern && pattern !== 'none' && !STANDARD_PATTERNS.includes(pattern);

        if (isCustom) {
          // Custom pattern is handled as an overlay in AppSidebar.js CSS
          sidebar.classList.add('sidebar-pattern-custom');
          console.log('Applied custom pattern:', pattern);
        } else {
          // Standard pattern
          sidebar.classList.add(patternClass);
          console.log('Sidebar pattern class added:', patternClass);
        }
        return true;
      }
      return false;
    };
    
    // Try to apply immediately
    if (!applyPattern()) {
      // If sidebar doesn't exist yet, retry after a short delay
      console.log('Sidebar not found, retrying...');
      setTimeout(() => {
        if (!applyPattern()) {
          // Try one more time after another delay
          setTimeout(applyPattern, 500);
        }
      }, 100);
    }
  };

  // Delete custom pattern
  const deleteCustomPattern = async (patternId) => {
    try {
      const token = localStorage.getItem('usertoken');
      if (!token) return;

      await axios.delete(`${config.API_BASE_URL}/api/user-preferences/custom-patterns/${patternId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh list
      loadCustomPatterns();
      
      // If deleted pattern was selected, revert to default
      const deletedPattern = customPatterns.find(p => p.id === patternId);
      if (deletedPattern && theme.backgroundPattern === deletedPattern.filename) {
        setTheme(prevTheme => ({ ...prevTheme, backgroundPattern: 'none' }));
        localStorage.setItem('userTheme', JSON.stringify({ ...theme, backgroundPattern: 'none' }));
      }
      return true;
    } catch (error) {
      console.error('Error deleting pattern:', error);
      return false;
    }
  };

  const value = {
    theme,
    setTheme: saveTheme, // Keeping original `setTheme: saveTheme`
    resetTheme,
    loading,
    error,
    customPatterns,
    refreshCustomPatterns: loadCustomPatterns,
    deleteCustomPattern, // Added deleteCustomPattern
    DEFAULT_THEME,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
